import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import BasicModal from "../../components/modals/BasicModal";
import {
  Box,
  Button,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  ArrowBackOutlined,
  ArrowForwardOutlined,
  SearchOutlined,
  ClearOutlined,
} from "@mui/icons-material";
import { blue, red } from "@mui/material/colors";
import Flx from "../../components/layout/Flx";
import { modalClear, modalSetData } from "../../redux/reducers/modalReducer";
import { saveCustomTooltipFields } from "../../utils/localStorage/loanTooltipFields";

const LoanPipelineTooltipDataKeyTransferModal = () => {
  const modalName = useSelector((state) => state.modal?.name);
  const modalData = useSelector((state) => state.modal?.data);
  const dispatch = useDispatch();

  const loanData = modalData?.loanData || {};
  const customFields = modalData?.customFields || [];

  const existingDataKeys = useMemo(() => {
    const baseKeys = [
      "expectedClosingDate",
      "interestRate",
      "baseLoanAmount",
      "totalLoanAmount",
      "totalHoldback",
      "originationPoints",
      "salesperson",
      "underwriter",
    ];
    const customKeys = customFields.map((field) => field.dataKey);
    return [...baseKeys, ...customKeys];
  }, [customFields]);

  const handleAddCustomField = useCallback(
    (dataKey) => {
      const updatedFields = [
        ...customFields,
        {
          dataKey,
          label: formatLabel(dataKey),
        },
      ];
      dispatch(
        modalSetData({
          loanData,
          customFields: updatedFields,
        }),
      );
    },
    [customFields, loanData, dispatch],
  );

  const handleDeleteCustomField = useCallback(
    (index) => {
      const updatedFields = customFields.filter((_, i) => i !== index);
      dispatch(
        modalSetData({
          loanData,
          customFields: updatedFields,
        }),
      );
    },
    [customFields, loanData, dispatch],
  );

  const onClose = useCallback(() => {
    dispatch(modalClear());
  }, [dispatch]);

  const onSave = useCallback(() => {
    // Save to localStorage
    const saved = saveCustomTooltipFields(customFields);

    if (saved) {
      console.log("Custom fields saved to localStorage:", customFields);
      // Dispatch custom event to notify tooltip components
      window.dispatchEvent(new Event("customFieldsUpdated"));
    } else {
      console.error("Failed to save custom fields to localStorage");
    }

    dispatch(modalClear());
  }, [customFields, dispatch]);

  if (!modalName || modalName !== "LOAN_PIPELINE_TOOLTIP_DATA_KEY_TRANSFER") {
    return null;
  }

  return (
    <BasicModal
      show
      maxWidth="md"
      title={"Edit Visible Fields"}
      // description={
      //   "Fields added here will appear in loan tooltips across the entire application. These settings are saved locally and apply to all loan previews, not just this specific loan."
      // }
      onClose={onClose}
    >
      <Box
        sx={{
          mb: 2,
          p: 1.5,
          borderRadius: "8px",
          backgroundColor: "#f0f9ff",
          border: "1px solid #bfdbfe70",
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontSize: "0.9em", color: "#1e40af", lineHeight: 1.5 }}
        >
          <strong>Note:</strong> Fields added here will appear in loan tooltips
          across the entire application. These settings are saved locally and
          apply to all loan previews, not just this specific loan.
        </Typography>
      </Box>
      <FieldTransferList
        data={loanData}
        existingKeys={existingDataKeys}
        customFields={customFields}
        onAdd={handleAddCustomField}
        onRemove={handleDeleteCustomField}
      />
      <Flx end gap={1}>
        <Button
          size="large"
          variant="outlined"
          color="primary"
          onClick={onClose}
        >
          Cancel Editing
        </Button>
        <Button
          size="large"
          variant="contained"
          color="primary"
          onClick={onSave}
        >
          Save & Update
        </Button>
      </Flx>
    </BasicModal>
  );
};

const FieldTransferList = ({
  data,
  existingKeys,
  customFields,
  onAdd,
  onRemove,
}) => {
  const [availableSearch, setAvailableSearch] = useState("");
  const [visibleSearch, setVisibleSearch] = useState("");

  const allAvailableKeys = useMemo(() => {
    const allKeys = Object.keys(data).sort();
    return allKeys.filter((key) => !existingKeys.includes(key));
  }, [data, existingKeys]);

  const filteredAvailableKeys = useMemo(() => {
    if (!availableSearch.trim()) return allAvailableKeys;

    const searchLower = availableSearch.toLowerCase();
    return allAvailableKeys.filter((key) => {
      const label = formatLabel(key).toLowerCase();
      const keyLower = key.toLowerCase();
      return label.includes(searchLower) || keyLower.includes(searchLower);
    });
  }, [allAvailableKeys, availableSearch]);

  const allSelectedKeys = useMemo(() => {
    return customFields.map((field) => field.dataKey);
  }, [customFields]);

  const filteredSelectedKeys = useMemo(() => {
    if (!visibleSearch.trim()) return allSelectedKeys;

    const searchLower = visibleSearch.toLowerCase();
    return allSelectedKeys.filter((key) => {
      const label = formatLabel(key).toLowerCase();
      const keyLower = key.toLowerCase();
      return label.includes(searchLower) || keyLower.includes(searchLower);
    });
  }, [allSelectedKeys, visibleSearch]);

  const handleAddField = useCallback(
    (key) => {
      onAdd(key);
    },
    [onAdd],
  );

  const handleRemoveField = useCallback(
    (key) => {
      const index = customFields.findIndex((field) => field.dataKey === key);
      if (index !== -1) {
        onRemove(index);
      }
    },
    [customFields, onRemove],
  );

  return (
    <Box sx={{ mb: 2 }}>
      <Flx g={2}>
        {/* Available Fields Column */}
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            maxHeight: 520,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 1,
              backgroundColor: "#f8fafc",
              borderBottom: "1px solid #e2e8f0",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.7em",
                fontWeight: 700,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                mb: 1,
                display: "block",
              }}
            >
              Available Fields ({filteredAvailableKeys.length})
            </Typography>
            <TextField
              size="small"
              placeholder="Search available..."
              value={availableSearch}
              onChange={(e) => setAvailableSearch(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined
                      sx={{ fontSize: "1em", color: "#94a3b8" }}
                    />
                  </InputAdornment>
                ),
                endAdornment: availableSearch && (
                  <InputAdornment position="end">
                    <ClearOutlined
                      sx={{
                        fontSize: "1em",
                        color: "#94a3b8",
                        cursor: "pointer",
                        "&:hover": { color: "#64748b" },
                      }}
                      onClick={() => setAvailableSearch("")}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "0.8em",
                  backgroundColor: "#ffffff",
                },
                "& .MuiOutlinedInput-input": {
                  py: 0.75,
                },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 0,
            }}
          >
            {filteredAvailableKeys.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.75em",
                    color: "#94a3b8",
                    fontStyle: "italic",
                  }}
                >
                  {availableSearch
                    ? "No fields match your search"
                    : "No fields available"}
                </Typography>
              </Box>
            ) : (
              filteredAvailableKeys.map((key) => (
                <ListItemButton
                  key={key}
                  onClick={() => handleAddField(key)}
                  sx={{
                    py: 0.75,
                    px: 1.5,
                    borderBottom: "1px solid #f1f5f9",
                    "&:hover": {
                      backgroundColor: blue[50],
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontSize: "0.8em",
                          fontWeight: 500,
                          lineHeight: 1,
                          // lineHeight: 1.4,
                          // mb: 0.25,
                          mt: 0.25,
                        }}
                      >
                        {formatLabel(key)}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.65em",
                          color: "#94a3b8",
                          lineHeight: 1,
                        }}
                      >
                        {key}
                      </Typography>
                    }
                  />
                  <ArrowForwardOutlined
                    sx={{ fontSize: "0.9em", color: blue[600], ml: 1 }}
                  />
                </ListItemButton>
              ))
            )}
          </Box>
        </Paper>

        {/* Selected Fields Column */}
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            maxHeight: 520,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 1,
              backgroundColor: "#f0f9ff",
              borderBottom: "1px solid #e2e8f0",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.7em",
                fontWeight: 700,
                color: "#64748b",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                mb: 1,
                display: "block",
              }}
            >
              Visible Fields ({filteredSelectedKeys.length})
            </Typography>
            <TextField
              size="small"
              placeholder="Search visible..."
              value={visibleSearch}
              onChange={(e) => setVisibleSearch(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlined
                      sx={{ fontSize: "1em", color: "#94a3b8" }}
                    />
                  </InputAdornment>
                ),
                endAdornment: visibleSearch && (
                  <InputAdornment position="end">
                    <ClearOutlined
                      sx={{
                        fontSize: "1em",
                        color: "#94a3b8",
                        cursor: "pointer",
                        "&:hover": { color: "#64748b" },
                      }}
                      onClick={() => setVisibleSearch("")}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  fontSize: "0.8em",
                  backgroundColor: "#ffffff",
                },
                "& .MuiOutlinedInput-input": {
                  py: 0.75,
                },
              }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              p: 0,
            }}
          >
            {filteredSelectedKeys.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.75em",
                    color: "#94a3b8",
                    fontStyle: "italic",
                  }}
                >
                  {visibleSearch
                    ? "No fields match your search"
                    : "No custom fields added"}
                </Typography>
              </Box>
            ) : (
              filteredSelectedKeys.map((key) => (
                <ListItemButton
                  key={key}
                  onClick={() => handleRemoveField(key)}
                  sx={{
                    py: 0.75,
                    px: 1.5,
                    borderBottom: "1px solid #f1f5f9",
                    "&:hover": {
                      backgroundColor: red[50],
                    },
                  }}
                >
                  <ArrowBackOutlined
                    sx={{ fontSize: "0.9em", color: red[600], mr: 1 }}
                  />
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontSize: "0.8em",
                          fontWeight: 500,
                          lineHeight: 1.2,
                          mb: 0.25,
                        }}
                      >
                        {formatLabel(key)}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.65em",
                          color: "#94a3b8",
                          lineHeight: 1,
                        }}
                      >
                        {key}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))
            )}
          </Box>
        </Paper>
      </Flx>
    </Box>
  );
};

const formatLabel = (key) => {
  return key
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .trim()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export default LoanPipelineTooltipDataKeyTransferModal;
